import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  armSellAudioAutoUnlock,
  fireCncProfitAlert,
  playLoudCncProfitBeep,
  requestSellNotifications,
  warmSellAudio
} from '../sell-alert-audio';
import {
  CNC_PIPELINE_STAGES,
  defaultCncBoard,
  formatRs,
  formatSpot,
  pipelineStageStatus,
  utcClockString
} from './cnc-degen-engine';
import './DegenDashboard.css';
import './CncDegenDashboard.css';
import DegenActionScreen from './DegenActionScreen';
import IstClock from '../IstClock';
import { formatIstDateTime, formatActivityWhen, formatCardOpenedAt } from '../ist-datetime';
import TradingCardWhen from '../TradingCardWhen';
import '../TradingActionBoard.css';

const POLL_MS = 3000;
const API = '/api/cnc-degen';

function WinStreakRing({ streak, nextSec }) {
  const pct = Math.min(100, (streak / 50) * 100);
  return (
    <div className="degen-streak-ring" aria-label={`${streak} win streak`}>
      <svg viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="52" className="degen-ring-bg" />
        <circle
          cx="60"
          cy="60"
          r="52"
          className="degen-ring-fill"
          strokeDasharray={`${pct * 3.27} 999`}
          transform="rotate(-90 60 60)"
        />
      </svg>
      <div className="degen-streak-inner">
        <strong>{streak}</strong>
        <span>DELIVERY WINS</span>
        <em>NEXT IN {nextSec}s</em>
      </div>
    </div>
  );
}

function WinStackChart({ data }) {
  const max = Math.max(...(data || []).map((d) => d.value), 1);
  return (
    <div className="degen-win-stack">
      {(data || []).map((bar) => (
        <div key={bar.label} className="degen-stack-bar-wrap" title={formatRs(bar.value)}>
          <div className="degen-stack-bar" style={{ height: `${(bar.value / max) * 100}%` }} />
        </div>
      ))}
    </div>
  );
}

function Pnl24Chart({ series }) {
  const rows = series?.length ? series : [{ t: 0, v: 0 }];
  const w = 420;
  const h = 140;
  const min = Math.min(...rows.map((p) => p.v));
  const max = Math.max(...rows.map((p) => p.v));
  const range = max - min || 1;
  const points = rows.map((p, i) => {
    const x = rows.length > 1 ? (i / (rows.length - 1)) * w : w / 2;
    const y = h - ((p.v - min) / range) * (h - 12) - 6;
    return `${x},${y}`;
  }).join(' ');
  const area = `0,${h} ${points} ${w},${h}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="degen-pnl24-svg" preserveAspectRatio="none">
      <defs>
        <linearGradient id="cncPnlGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(13, 110, 79, 0.45)" />
          <stop offset="100%" stopColor="rgba(13, 110, 79, 0.02)" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#cncPnlGrad)" />
      <polyline points={points} fill="none" stroke="#0d6e4f" strokeWidth="2" />
    </svg>
  );
}

function CandlestickChart({ candles, price, symbol }) {
  const rows = candles?.length ? candles : [];
  const w = 640;
  const h = 200;
  if (!rows.length) {
    return (
      <div className="degen-candle-wrap">
        <div className="degen-spot-head">
          <span>LIVE {symbol || 'NIFTY'} · NSE INDEX</span>
          <strong>{formatSpot(price)}</strong>
        </div>
        <p className="degen-chart-loading">Loading live chart…</p>
      </div>
    );
  }
  const lows = rows.map((c) => c.low);
  const highs = rows.map((c) => c.high);
  const min = Math.min(...lows);
  const max = Math.max(...highs);
  const range = max - min || 1;
  const barW = w / rows.length;

  return (
    <div className="degen-candle-wrap">
      <div className="degen-spot-head">
        <span>LIVE {symbol || 'NIFTY'} · NSE INDEX · CNC context</span>
        <strong className={rows[rows.length - 1]?.bull ? 'up' : 'down'}>{formatSpot(price)}</strong>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="degen-candle-svg" preserveAspectRatio="none">
        {rows.map((c, i) => {
          const x = i * barW + barW * 0.2;
          const bw = barW * 0.6;
          const y = (v) => h - ((v - min) / range) * (h - 16) - 8;
          const color = c.bull ? '#0d6e4f' : '#b83232';
          return (
            <g key={`c-${i}`}>
              <line x1={x + bw / 2} y1={y(c.high)} x2={x + bw / 2} y2={y(c.low)} stroke={color} strokeWidth="1" />
              <rect x={x} y={Math.min(y(c.open), y(c.close))} width={bw} height={Math.max(2, Math.abs(y(c.open) - y(c.close)))} fill={color} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function PipelineStrip({ state }) {
  return (
    <section className="degen-pipeline">
      <header>
        <h3>6-Cycle CNC Delivery Pipeline</h3>
        <span>NSE + BSE · swing scan → CNC BUY → hold until target</span>
      </header>
      <div className="degen-pipeline-row">
        {CNC_PIPELINE_STAGES.map((stage, i) => {
          const status = pipelineStageStatus(state.activeStage, i);
          const meta = state.cycleMeta?.[stage.key] || {};
          return (
            <div key={stage.key} className={`degen-pipe-card ${status}`}>
              <div className="degen-pipe-num">{stage.id}</div>
              <strong>{stage.label}</strong>
              <p>{meta.detail || stage.desc}</p>
              <small>{meta.sub || (status === 'active' ? `${Math.round(state.stageProgress || 0)}%` : '—')}</small>
              {status === 'active' && (
                <div className="degen-pipe-progress">
                  <div style={{ width: `${state.stageProgress || 0}%` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ScanTable({ rows }) {
  if (!rows?.length) {
    return <p className="degen-scan-empty">Scanning NSE + BSE for CNC delivery picks…</p>;
  }
  return (
    <div className="degen-scan-table-wrap">
      <table className="degen-scan-table">
        <thead>
          <tr>
            <th>Stock</th>
            <th>Exch</th>
            <th>Price</th>
            <th>Δ%</th>
            <th>RSI</th>
            <th>Score</th>
            <th>Dir</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 8).map((r) => (
            <tr key={r.symbol} className={r.qualifies ? 'qualifies' : ''} title={r.qualifies ? 'Qualified — CNC BUY candidate' : ''}>
              <td>{r.stock}</td>
              <td><span className={`degen-exch degen-exch-${(r.exchange || 'NSE').toLowerCase()}`}>{r.exchange}</span></td>
              <td>₹{Number(r.price).toFixed(2)}</td>
              <td className={r.changePct >= 0 ? 'up' : 'down'}>{r.changePct}%</td>
              <td>{r.rsi}</td>
              <td>{r.score}</td>
              <td className="up">{r.direction}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CncPositionsPanel({ positions }) {
  if (!positions?.length) {
    return <p className="degen-scan-empty">No open CNC delivery positions</p>;
  }
  return (
    <ul className="degen-pos-list degen-pos-list-cards">
      {positions.map((p) => {
        const move = p.currentPrice != null
          ? Math.round((p.currentPrice - p.entryPrice) * 100) / 100
          : null;
        const movePct = move != null && p.entryPrice
          ? Math.round((move / p.entryPrice) * 10000) / 100
          : null;
        return (
          <li key={p.symbol} className={`degen-pos-card ${p.tab === 'hold' ? 'degen-pos-hold' : ''}`}>
            <div className="degen-pos-card-head">
              <span className="degen-cnc-badge">CNC</span>
              {p.tab === 'hold' && <span className="degen-hold-badge">HOLD</span>}
              <span className={`degen-exch degen-exch-${(p.exchange || 'NSE').toLowerCase()}`}>{p.exchange}</span>
              <strong>{p.stock}</strong>
              <span>LONG ×{p.qty}</span>
            </div>
            <hr className="tab-card-divider cnc-action-divider" aria-hidden="true" />
            <TradingCardWhen item={p} prefix="Opened" />
            <div className="degen-pos-card-body">
              <span>@ ₹{Number(p.entryPrice).toFixed(2)}</span>
              {p.holdDays != null && <span>{p.holdDays}d hold</span>}
              {move != null && (
                <span className={move >= 0 ? 'up' : 'down'}>
                  {move >= 0 ? '+' : ''}₹{move}/sh ({movePct}%)
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function CncActionScreen({ guide, marketOpen, sellAlertsOpen = true, onConfirmSold, confirmSoldBusy }) {
  return (
    <DegenActionScreen
      guide={guide}
      marketOpen={marketOpen}
      sellAlertsOpen={sellAlertsOpen}
      mode="cnc"
      title="Your CNC action screen"
      onConfirmSold={onConfirmSold}
      confirmSoldBusy={confirmSoldBusy}
    />
  );
}

export default function CncDegenDashboard() {
  const [board, setBoard] = useState(defaultCncBoard);
  const [utc, setUtc] = useState(utcClockString());
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmSoldBusy, setConfirmSoldBusy] = useState(false);
  const [showExtras, setShowExtras] = useState(false);
  const profitBeepedRef = useRef(new Set());
  const profitBuzzIntervalRef = useRef(null);

  const profitSells = useMemo(() => {
    const sellAlertsOpen = board.sellAlertsOpen !== false && board.marketOpen !== false;
    if (!sellAlertsOpen) return [];
    return (board.actionGuide?.sell || []).filter((s) => s.profitHit && !s.urgent);
  }, [board.actionGuide?.sell, board.sellAlertsOpen, board.marketOpen]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/board`, { timeout: 12000 });
      setBoard(data);
      setError(data.ok === false ? (data.error || 'Board error') : null);
    } catch (e) {
      setError(e.message || 'Cannot reach CNC backend — is port 9000 running?');
    }
  }, []);

  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, POLL_MS);
    return () => clearInterval(poll);
  }, [refresh]);

  useEffect(() => {
    const clock = setInterval(() => setUtc(utcClockString()), 1000);
    return () => clearInterval(clock);
  }, []);

  useEffect(() => {
    requestSellNotifications();
    const disarm = armSellAudioAutoUnlock();
    warmSellAudio();
    return disarm;
  }, []);

  useEffect(() => {
    for (const row of profitSells) {
      const sk = row.stock || row.symbol;
      if (!sk || profitBeepedRef.current.has(sk)) continue;
      profitBeepedRef.current.add(sk);
      fireCncProfitAlert({ stock: sk, movePct: row.movePct });
    }
    const activeKeys = new Set(profitSells.map((r) => r.stock || r.symbol).filter(Boolean));
    for (const sk of [...profitBeepedRef.current]) {
      if (!activeKeys.has(sk)) profitBeepedRef.current.delete(sk);
    }
    if (!profitSells.length && profitBuzzIntervalRef.current) {
      clearInterval(profitBuzzIntervalRef.current);
      profitBuzzIntervalRef.current = null;
    }
  }, [profitSells]);

  useEffect(() => {
    const anyProfit = profitSells.length > 0;
    if (anyProfit && !profitBuzzIntervalRef.current) {
      profitBuzzIntervalRef.current = setInterval(() => playLoudCncProfitBeep(), 10000);
    } else if (!anyProfit && profitBuzzIntervalRef.current) {
      clearInterval(profitBuzzIntervalRef.current);
      profitBuzzIntervalRef.current = null;
    }
    return () => {
      if (profitBuzzIntervalRef.current) {
        clearInterval(profitBuzzIntervalRef.current);
        profitBuzzIntervalRef.current = null;
      }
    };
  }, [profitSells.length]);

  const confirmSold = useCallback(async (symbol) => {
    const sk = symbol || '';
    if (!sk) return;
    setConfirmSoldBusy(true);
    try {
      const { data } = await axios.post(`${API}/confirm-sold`, { symbol: sk });
      setBoard(data);
      profitBeepedRef.current.delete(sk);
      if (profitBuzzIntervalRef.current) {
        clearInterval(profitBuzzIntervalRef.current);
        profitBuzzIntervalRef.current = null;
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirmSoldBusy(false);
    }
  }, []);

  const togglePipeline = async () => {
    setBusy(true);
    try {
      const url = board.pipelineRunning ? `${API}/stop` : `${API}/start`;
      const { data } = await axios.post(url);
      setBoard(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const monthLabel = useMemo(() => (board.month || '').toUpperCase(), [board.month]);
  const statusPill = board.marketOpen ? 'LIVE · CNC' : 'MARKET CLOSED · HOLDS KEPT';

  return (
    <div className="degen-app cnc-degen-app">
      <header className="degen-topbar">
        <div className="degen-top-left">
          <span className="degen-wallet">{formatRs(board.cash)} cash</span>
          <span className="degen-tag degen-tag-mode">{board.mode || 'CNC DELIVERY'}</span>
          <span className="degen-tag degen-tag-cnc">DELIVERY / CNC</span>
          <span className="degen-tag degen-tag-pipe">{board.pipelineLabel}</span>
          <span className={`degen-tag degen-tag-live ${board.marketOpen ? 'open' : ''}`}>{statusPill}</span>
        </div>
        <div className="degen-top-right degen-nav-links">
          <Link to="/ledger/cnc" className="degen-nav-link">Buy/Sell Ledger</Link>
          <span className="degen-utc">UTC {utc}</span>
          <span className="degen-utc ist-dt"><IstClock /></span>
          {board.istTime && board.istTime !== '' && (
            <span className="degen-utc degen-utc-muted" title="Last server sync">
              Srv {formatIstDateTime(board.istTime)}
            </span>
          )}
        </div>
      </header>

      {error && <div className="degen-error-banner">{error}</div>}

      <CncActionScreen
        guide={board.actionGuide}
        marketOpen={board.marketOpen}
        sellAlertsOpen={board.sellAlertsOpen !== false}
        onConfirmSold={confirmSold}
        confirmSoldBusy={confirmSoldBusy}
      />

      <div className="mobile-kpi-strip" aria-label="CNC session">
        <div className="mobile-kpi-cell">
          <span>P&amp;L</span>
          <strong className={(board.realizedPnl || 0) >= 0 ? 'up' : 'down'}>{formatRs(board.realizedPnl)}</strong>
        </div>
        <div className="mobile-kpi-cell">
          <span>Cash</span>
          <strong>{formatRs(board.cash)}</strong>
        </div>
        <div className="mobile-kpi-cell">
          <span>Slots</span>
          <strong>{board.activeSlots ?? 0}/{board.maxPositions ?? 5}</strong>
        </div>
        <div className="mobile-kpi-cell">
          <span>Win</span>
          <strong>{board.winRate}%</strong>
        </div>
      </div>

      <section className="degen-hero mobile-secondary-hero">
        <div className="degen-hero-main">
          <p className="degen-hero-label">CNC DELIVERY P&amp;L · {monthLabel} · PAPER · NSE/BSE ONLY</p>
          <h1 className="degen-hero-pnl">{formatRs(board.realizedPnl)}</h1>
          <div className="degen-hero-stats">
            <span>{board.tradeCount?.toLocaleString()} CNC trades</span>
            <span>{board.winRate}% win</span>
            <span>{board.universeSize} symbols ({board.nseCount} NSE · {board.bseCount} BSE)</span>
          </div>
          <p className="degen-last-action">{board.lastAction}</p>
        </div>
        <WinStreakRing streak={board.winStreak || 0} nextSec={board.nextCycleSec || 0} />
      </section>

      <button
        type="button"
        className="mobile-extras-toggle"
        onClick={() => setShowExtras((v) => !v)}
        aria-expanded={showExtras}
      >
        {showExtras ? 'Hide charts & scan' : 'Show charts · scan · positions'}
      </button>

      <div className={`mobile-extras-panel${showExtras ? ' is-open' : ''}`}>
      <section className="degen-mid-grid">
        <div className="degen-card degen-cashflow">
          <header>
            <h3>CASH FLOW</h3>
            <span>{monthLabel} · CNC WIN STACK</span>
          </header>
          <p className="degen-card-pnl">{formatRs(board.totalDayPnl ?? board.realizedPnl)}</p>
          <WinStackChart data={board.winStack} />
        </div>

        <div className="degen-card degen-pnl-live">
          <header>
            <h3>PORTFOLIO PNL</h3>
            <span>Unrealized {formatRs(board.unrealizedPnl || 0)}</span>
          </header>
          <Pnl24Chart series={board.pnl24} />
        </div>
      </section>

      <section className="degen-spot-section degen-card">
        <CandlestickChart candles={board.candles} price={board.spotPrice} symbol={board.spotSymbol} />
      </section>

      <section className="degen-card degen-scan-section">
        <header className="degen-scan-head">
          <h3>NSE + BSE Delivery Scan</h3>
          <span>UP only · ₹{board.minSharePrice ?? 10}–₹{board.maxSharePrice ?? 100} · RSI · min score 60</span>
        </header>
        <ScanTable rows={board.scanResults} />
      </section>

      <section className="degen-card degen-pos-section">
        <header className="degen-scan-head">
          <h3>Open CNC Positions</h3>
          <span>
            Always +{board.profitTargetPct ?? 1}% sell · {board.activeSlots ?? 0}/{board.maxPositions ?? 5} active slots ·
            10m → HOLD (no stop) · ₹{board.minSharePrice ?? 10}–₹{board.maxSharePrice ?? 100}
          </span>
        </header>
        <CncPositionsPanel positions={board.positions} />
      </section>

      <PipelineStrip state={board} />
      </div>
      <footer className="degen-footer-metrics">
        <div className="degen-metric">
          <span>CNC TRADES · {monthLabel}</span>
          <strong>{board.binsApril ?? board.tradeCount}</strong>
        </div>
        <div className="degen-metric">
          <span>NEXT CNC BUY</span>
          <strong className="up">
            {board.lastPick
              ? `${board.lastPick.exchange} ${board.lastPick.stock} UP`
              : `UP · score ${board.leverage}`}
          </strong>
        </div>
        <div className="degen-metric">
          <span>QTY · WIN%</span>
          <strong>{board.stockQty ?? 50} sh · {board.velocity1h ?? board.winRate}%</strong>
        </div>
        <button type="button" className="degen-toggle" onClick={togglePipeline} disabled={busy}>
          {board.pipelineRunning ? 'Pause CNC pipeline' : 'Start CNC trading'}
        </button>
      </footer>

      {board.activityLog?.length > 0 && (
        <section className="degen-card degen-log-section">
          <h3>Activity</h3>
          <ul className="degen-log">
            {board.activityLog.map((row, i) => (
              <li key={`${row.time}-${i}`} className={row.type}>{formatActivityWhen(row)} — {row.msg}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
